// THE WORKSPACE'S PROJECTS IN AN AI ANSWER — the `canvas-projects` and
// `project-status` views the assistant mounts in its thread and a Fly page
// places as sections (workspace functions/assistantAppViews.js, the `views[]`
// contract).
//
// canvasAiViews() is the view PROVIDER. Unlike calendar / tickets it needs no
// fold: its components (components/CanvasProjectsView.js, ProjectStatusView.js)
// ride the canvas EAGER slice with these functions, so `fold` is null and the
// card mounts them at once.
//
//   canvas-projects   the workspace's WEBSITES (the app split, docs/APP_SPLIT.md:
//                     never a workspace app, never a library), newest
//                     first; `search` matches the name or key, `env` is the
//                     environment a card's live preview opens. A card opens
//                     that project's LIVE PREVIEW in the sheet (⌘ / Ctrl: in a
//                     split); its row of actions opens the canvas or the
//                     project's analytics; its Studio button opens the editor.
//   project-status    the websites' state (functions/canvasStatus.js): with no
//                     project named, EVERY website and whether each is live,
//                     never published or off (QA 2026-10-19: "What is the
//                     status of our sites?" showed one site beside a projects
//                     list with no state); with `project`, one site — what
//                     each environment serves, its recent versions. The
//                     projects view carries no state: its describe says
//                     where the state is.
//
// The projects are the home Projects widget's own: the active org's projects
// (`root._projectsForActiveOrg`, read by the home) kept to the active
// workspace — or, when no home read them yet, the same SDK call, whose list
// then lands on that root key for every other reader (one read per org). The
// site views of the preview package read the same list through
// canvasAnswerRead / canvasAnswerSites, and what an asked name means through
// canvasAnswerNamed (their loads: canvasAnswerNamedRead, which reads the App
// Store list once when no exact name matches, and canvasAnswerNamedMoved,
// which decides again when a list lands after the card answered).
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

export function canvasAiViews() {
  const el = this && typeof this.call === 'function' ? this : null
  const root = (el && el.state && el.state.root) || null
  // The site a status names: one of the workspace's websites, as the site
  // views take it (an enum of their names once the list is known).
  let project = {
    type: 'string',
    describe: 'a website (project) name or key; the project on screen, else the newest website'
  }
  if (el && root) {
    try {
      const own = el.call('canvasAnswerProjectParam', root)
      if (own && typeof own === 'object') project = own
    } catch (_) {
      /* the string form stays */
    }
  }
  return {
    app: {
      id: 'system/canvas',
      navbar: [{ label: 'shell.app.studio', route: '/studio', icon: 'layers' }],
      apps: [{ icon: 'layers' }]
    },
    fold: null,
    views: [
      {
        id: 'canvas-projects',
        title: 'canvas.answer.title',
        describe:
          "The workspace's websites (never its installed apps) as cards, newest first; search = words in the name. A card opens its live preview. State: project-status.",
        component: 'CanvasProjectsView',
        icon: 'layers',
        hue: 'purple',
        size: 'l',
        params: {
          search: { type: 'string', describe: 'words in the project name or key' },
          limit: { type: 'number', min: 1, max: 24, default: 6 },
          env: {
            type: 'enum',
            values: ['production', 'staging', 'development'],
            labels: {
              production: 'canvas.sidebar.env.production',
              staging: 'canvas.sidebar.env.staging',
              development: 'canvas.sidebar.env.development'
            },
            default: 'production',
            describe: 'the environment a live preview opens'
          }
        },
        link: { route: '/studio', query: {} }
      },
      {
        id: 'project-status',
        title: 'canvas.status.title',
        describe:
          "Whether the workspace's websites are live, never published or off: every website, or with project one site's environments and recent versions. Read-only.",
        component: 'ProjectStatusView',
        icon: 'version',
        hue: 'purple',
        size: 'l',
        params: {
          // Left out: EVERY website (functions/canvasStatus.js).
          project: {
            ...project,
            describe: 'one website of this workspace; leave it out to show every website'
          },
          limit: { type: 'number', min: 1, max: 10, default: 5, describe: 'recent versions shown' }
        },
        link: { route: '/studio', call: 'canvasStatusLink' }
      }
    ]
  }
}

// The workspace's projects out of the active org's list (`list`, else
// `root._projectsForActiveOrg`) — the home's own rule: a project whose
// workspace is the active one. null when no list was read yet.
export function canvasAnswerProjects(root, list) {
  const r = root && typeof root === 'object' ? root : {}
  const all = Array.isArray(list)
    ? list
    : Array.isArray(r._projectsForActiveOrg)
      ? r._projectsForActiveOrg
      : null
  if (!all) return null
  const claims = r.jwtClaims || {}
  const ws = r.workspace || {}
  const activeWsId = claims.active_workspace_id || ws.id || ws._id || null
  if (!activeWsId) return all.filter(Boolean)
  return all.filter((p) => {
    const w = p && p.workspace
    const wsId = (w && (w.id || w._id)) || w
    return String(wsId) === String(activeWsId)
  })
}

// The projects for a view: the home's list when it has one, else the same SDK
// call the home makes (`sdk.getProjects({ owner: <org slug> })`). The list a
// view had to read lands on `root._projectsForActiveOrg` — the home's key —
// when the org is still the one it was read for, so the next reader (another
// view, the catalog's site list) asks nothing; reads in flight for one org are
// shared (the root element's scope). `{ projects, failed }`.
export async function canvasAnswerRead(el) {
  const host = el || this
  const root = (host && host.state && host.state.root) || {}
  const have = host.call('canvasAnswerProjects', root)
  if (have) return { projects: have, failed: false }
  const claims = root.jwtClaims || {}
  const orgs = claims.orgs || root.orgs || []
  const orgId =
    claims.workspace_org_id || (Array.isArray(root.orgs) && root.orgs[0] && root.orgs[0].id)
  const org = (Array.isArray(orgs) ? orgs : []).find((o) => o && o.id === orgId)
  const sdk = host.sdk || host.call('getSDK')
  if (!org || !org.slug || !sdk || typeof sdk.getProjects !== 'function') {
    return { projects: [], failed: true }
  }
  let rootEl = null
  try {
    rootEl = typeof host.getRoot === 'function' ? host.getRoot() : null
  } catch (_) {
    rootEl = null
  }
  const memo = rootEl && rootEl.scope && typeof rootEl.scope === 'object' ? rootEl.scope : null
  const slot = memo && memo.canvasAnswerReadInFlight
  let pending = slot && slot.org === org.slug ? slot.promise : null
  if (!pending) {
    pending = sdk.getProjects({ owner: org.slug, limit: 500 }).then((res) => {
      const raw = Array.isArray(res) ? res : (res && (res.data || res.items)) || []
      const now = root.jwtClaims || {}
      const stillOrg =
        (now.workspace_org_id || (Array.isArray(root.orgs) && root.orgs[0] && root.orgs[0].id)) ===
        orgId
      if (stillOrg && typeof root.update === 'function' && !root._projectsForActiveOrg) {
        root.update({ _projectsForActiveOrg: raw }, { preventFetch: true })
      }
      return raw
    })
    if (memo) {
      memo.canvasAnswerReadInFlight = { org: org.slug, promise: pending }
      const clear = () => {
        if (memo.canvasAnswerReadInFlight && memo.canvasAnswerReadInFlight.promise === pending) {
          memo.canvasAnswerReadInFlight = null
        }
      }
      pending.then(clear, clear)
    }
  }
  try {
    const raw = await pending
    return { projects: host.call('canvasAnswerProjects', root, raw) || [], failed: false }
  } catch (_) {
    return { projects: [], failed: true }
  }
}

// THE WORKSPACE'S WEBSITES — the projects a site view may show: never a
// shared library (`isSharedLibrary`, projectType 'library') and never an app
// INSTALLED in this workspace (a module: its pages live in the shell, it
// serves no site of its own — `apps`, `owner/key` ids; dev Keepz 2026-10-18:
// the console module drew a blank frame). Projects of type 'website' first —
// "our website" is never an internal app (QA 2026-10-18: system picked bi-app,
// astio-ai picked greenig-app) — then the newest first. Each row:
// `{ key, name, owner, id, projectType, updatedAt }`, the owner as a slug (an
// ObjectId owner falls back to the workspace's organization).
export function canvasAnswerSites(projects, apps) {
  const slugOf = (v) => {
    if (!v) return ''
    if (typeof v === 'string') return /^[a-f0-9]{24}$/i.test(v) ? '' : v
    return v.slug || v.key || ''
  }
  const installed = new Set((Array.isArray(apps) ? apps : []).filter(Boolean).map(String))
  // THE APP SPLIT (docs/APP_SPLIT.md): a site view shows WEBSITES only, and
  // the shared projectKind decides — with the workspace's own signals (its
  // installed apps, its AI-built apps, the App Store apps), so an App Store
  // module that is NOT installed here is still never "our website". With no
  // element at hand (a direct call), the installed ids and the library flag
  // are the rule.
  const el = this && typeof this.call === 'function' ? this : null
  const kindRoot = el && el.state && el.state.root
  let kindCtx = null
  if (el && kindRoot) {
    try {
      const own = el.call('projectKindContext', kindRoot) || {}
      kindCtx = {
        installed: [...installed, ...(own.installed || [])],
        extensions: own.extensions || [],
        modules: own.modules || []
      }
    } catch (_) {
      kindCtx = null
    }
  }
  const at = (x) =>
    Date.parse(x.updatedAt || x.updated_at || x.createdAt || x.created_at || '') || 0
  const rows = []
  for (const p of Array.isArray(projects) ? projects : []) {
    if (!p || !p.key) continue
    if (p.isSharedLibrary === true || p.projectType === 'library') continue
    const owner = slugOf(p.owner) || slugOf(p.workspace && p.workspace.organization)
    if (installed.has(owner + '/' + String(p.key))) continue
    if (kindCtx) {
      // An unregistered classifier answers nothing — the row then stays.
      const kind = el.call('projectKind', p, kindCtx)
      if (typeof kind === 'string' && kind !== 'website') continue
    }
    rows.push({
      key: String(p.key),
      name: typeof p.name === 'string' && p.name.trim() ? p.name.trim() : String(p.key),
      owner,
      id: p._id || p.id || null,
      projectType: p.projectType || null,
      updatedAt: p.updatedAt || p.updated_at || p.createdAt || p.created_at || null,
      at: at(p)
    })
  }
  rows.sort((a, b) => {
    const wa = a.projectType === 'website' ? 0 : 1
    const wb = b.projectType === 'website' ? 0 : 1
    return wa - wb || b.at - a.at
  })
  return rows.map(({ at: _at, ...row }) => row)
}

// The installed apps of this workspace as `owner/key` ids (the takeover module
// and every installed app) — the projects a site view never shows.
export function canvasAnswerInstalled(root) {
  const r = root && typeof root === 'object' ? root : {}
  const ids = []
  const wp = r.workspaceProject
  if (wp && wp.id) ids.push(String(wp.id))
  for (const a of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) {
    if (a && a.id) ids.push(String(a.id))
  }
  return ids
}

// THE PROJECT ON SCREEN — the default site of a site view: the project the
// preview shows (`root._previewParams`, the Studio sidebar's carrier), else
// the one the address names (`?owner&key` — the /preview route, the sheet's
// own route through readPageParam), else the project the canvas editor has
// open (`root.data`) when its ROUTE names it. The route is the root's route
// signal (`currentPath`, kept by AppShell's history sync) read the way the
// canvas loader reads it (the shared resolveProjectParams: the workspace form
// `/w/<org>[/<ws>]/p/<key>` and the legacy `/<owner>/<key>`). The owner is the
// route's own (the one the loader read the project by: the org on the
// workspace form), else the record's slug; never a 24-hex id. WHY, dev
// 2026-10-31 (W28 bundle), the Velvet Lantern canvas at
// `/w/kestrel-assist/default/p/velvet-lantern`: `root.data` had key
// `velvet-lantern`, projectType `app` and NO owner field. The old reading
// took segment 1 (the org) for the key and the owner from the record alone,
// so it returned null, and the site cards fell back to the newest website
// (Oak Crumb Bakery). `{ owner, key }` or null.
export function canvasAnswerOnScreen(root) {
  const r = root && typeof root === 'object' ? root : {}
  const clean = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')
  const p = r._previewParams
  if (p && clean(p.owner) && clean(p.key)) return { owner: clean(p.owner), key: clean(p.key) }
  const fns = this && this.context && this.context.functions
  if (fns && typeof fns.readPageParam === 'function') {
    let owner = ''
    let key = ''
    try {
      owner = clean(this.call('readPageParam', 'owner'))
      key = clean(this.call('readPageParam', 'key'))
    } catch (_) {
      owner = ''
      key = ''
    }
    if (owner && key) return { owner, key }
  }
  const path = clean(r.currentPath)
  const d = r.data && typeof r.data === 'object' ? r.data : null
  const key = d ? clean(d.key) || clean(d.projectKey) : ''
  if (!key || !path || !this || typeof this.call !== 'function') return null
  let route = null
  try {
    route = this.call('resolveProjectParams', {
      loc: { pathname: path, search: '', hostname: '' }
    })
  } catch (_) {
    route = null
  }
  const routeKey = route && typeof route === 'object' ? clean(route.key) : ''
  if (!routeKey || routeKey.split('.')[0] !== key.split('.')[0]) return null
  const slug = (v) => (v && !/^[a-f0-9]{24}$/i.test(v) ? v : '')
  const o = d.owner
  const own = clean(typeof o === 'string' ? o : o && (o.slug || o.username))
  const owner = slug(clean(route.owner)) || slug(own)
  return owner ? { owner, key } : null
}

// WHICH WEBSITE a site view shows (`sites` = canvasAnswerSites rows): the one
// `query` names — its key, then its name (then its `label`, where a row has
// one: an App Store app's name as a person sees it, canvasAnswerStoreApps),
// exact first, then a part of one —
// else, with no query, the project on screen when it is one of them, else,
// with NO project on screen, the first (a website before an app, the newest
// first). A project on screen that is not one of them (a workspace app, a
// library, another workspace's project) is never swapped for another website
// in silence: null, and the view says that no website matches (dev
// 2026-10-31: on the canvas of the app Velvet Lantern, the pages card showed
// the website Oak Crumb Bakery). null too when the query names none of them,
// or no website is left. `how` (optional) keeps a query to one kind of
// match: 'exact' (its key or its name) or 'part' (a part of one) —
// canvasAnswerNamed asks each kind across every list in turn; left out,
// both, exact first. 'span' is 'part' that also says where the part sits in
// the asked text: `{ row, from, to }` (word indexes of the asked text, `to`
// excluded), null when no part matches or no text is asked.
// A PART is whole words, in both directions (the concierge, 2026-10-31): the
// asked text inside a name ("Velvet" names "Velvet Lantern") or a name inside
// the asked text ("Velvet Lantern-ის საიტი" names it) — the words of the one
// in order, each the same word as the next word of the other, with any signs
// between (a hyphen, a dot, a space). Never a piece of a word: "mobile" does
// not name "BI", "art" does not name "Smart Home". A text of under 3 letters
// names nothing by part (an exact "BI" still names "BI"). A Georgian word of
// 3+ letters is also the same word as its case forms, either way round (the
// concierge's pick, 2026-10-31) — the word, or its stem without a final ი or
// ა, plus one ending of a closed list, from the start of the word: "სახლის"
// and "სახლში" name "სახლი", "სახლის" names "დიდი სახლი", "ნინო" names
// "ნინოს სალონი"; "სახლთმფლობელი" is not "სახლი", "ნინო" does not name
// "ტონინოს პიცა". No ო: nobody asks for a project in the vocative, and
// "ბესო" is a given name. Syncopating stems ("მასწავლებელი" →
// "მასწავლებლის") are a known gap. wordNames is the ONE word test (`same`
// asks it both ways round): a stem step goes there.
// THE LONGEST PART WINS when more than one row matches by part (workspace ai,
// 2026-10-31): the longest SPAN — the words of the asked text a part covers:
// all of them when the asked text sits inside a name, the name's own words,
// where they sit, when the name sits inside the asked text; the longest of
// key, name and label — then the list order. "CRM Landing site" names "CRM
// Landing", not "Landing".
export function canvasAnswerPickSite(sites, query, onScreen, how) {
  const list = (Array.isArray(sites) ? sites : []).filter((x) => x && x.key)
  const low = (v) =>
    String(v == null ? '' : v)
      .trim()
      .toLowerCase()
  const q = low(query)
  const names = (x) => [low(x.key), low(x.name), low(x.label)]
  if (q) {
    if (how !== 'part' && how !== 'span') {
      const exact = list.find((x) => names(x).includes(q))
      if (exact || how === 'exact') return exact || null
    }
    const ENDINGS = [
      '',
      'ს',
      'ის',
      'ით',
      'ად',
      'მა',
      'მ',
      'ში',
      'ზე',
      'თან',
      'სთან',
      'დან',
      'იდან',
      'მდე',
      'ამდე',
      'სთვის',
      'ისთვის',
      'სგან',
      'ისგან',
      'სკენ',
      'ისკენ',
      'ვით',
      'ივით',
      'ები',
      'ებს',
      'ების',
      'ებით',
      'ებად',
      'ებმა',
      'ებში',
      'ებზე',
      'ებთან',
      'ებიდან',
      'ებისთვის',
      'ებისგან'
    ]
    // Does the word `n` name the word `w`? The same word; or, for a word of
    // Georgian letters only, 3+ letters: `w` = the word or its stem (no final
    // ი / ა, 3+ letters) + one ending of the list.
    const wordNames = (n, w) => {
      if (n === w) return true
      const chars = [...n]
      if (chars.length < 3 || !/^[Ⴀ-ჿᲐ-Ჿ]+$/u.test(n)) return false
      const stems = [n]
      const last = chars[chars.length - 1]
      if ((last === 'ი' || last === 'ა') && chars.length > 3) {
        stems.push(chars.slice(0, -1).join(''))
      }
      return stems.some((b) => w.startsWith(b) && ENDINGS.includes(w.slice(b.length)))
    }
    // The same word, either way round — ONE test, ONE list (the concierge's
    // pick, 2026-10-31): an asked "სახლის" names "დიდი სახლი" as an asked
    // "სახლი" names "სახლის საიტი".
    const same = (a, b) => wordNames(a, b) || wordNames(b, a)
    // The words of a text: its runs of letters and digits.
    const words = (v) => v.split(/[^\p{L}\p{N}]+/u).filter(Boolean)
    // The words of `needle`, in order, inside the words of `hay`: the index
    // of the word of `hay` it starts at, else -1; a needle of under 3 letters
    // never.
    const inside = (needle, hay) => {
      const nw = words(needle)
      const hw = words(hay)
      if (!nw.length || [...nw.join('')].length < 3) return -1
      for (let i = 0; i + nw.length <= hw.length; i++) {
        if (nw.every((n, k) => same(n, hw[i + k]))) return i
      }
      return -1
    }
    // A row's span: `[from, to)` in the words of the asked text — all of them
    // when the asked text sits inside its key, name or label; that name's
    // words, where they sit, when the name sits inside the asked text — the
    // longest of its names; null when no part matches.
    const asked = words(q).length
    const spanOf = (x) => {
      let best = null
      for (const n of names(x)) {
        if (!n) continue
        const at = inside(n, q)
        const found = [
          inside(q, n) >= 0 ? [0, asked] : null,
          at >= 0 ? [at, at + words(n).length] : null
        ]
        for (const s of found) {
          if (s && (!best || s[1] - s[0] > best[1] - best[0])) best = s
        }
      }
      return best
    }
    // The longest span wins; a tie keeps the list order.
    let best = null
    let span = null
    for (const x of list) {
      const s = spanOf(x)
      if (s && (!span || s[1] - s[0] > span[1] - span[0])) {
        best = x
        span = s
      }
    }
    if (how === 'span') return best ? { row: best, from: span[0], to: span[1] } : null
    return best
  }
  if (how === 'span') return null
  const on = onScreen && typeof onScreen === 'object' ? onScreen : null
  if (on && on.key) {
    const k = String(on.key).split('.')[0]
    const hit = list.find(
      (x) => String(x.key).split('.')[0] === k && (!on.owner || !x.owner || x.owner === on.owner)
    )
    return hit || null
  }
  return list[0] || null
}

// THE APP STORE APPS THIS WORKSPACE KNOWS ALREADY — no read of its own: the
// App Store list the shell read (`root._wsAppsCatalog`: /add-app, the apps
// hub, the welcome, the search with no result — each row named) and the App
// Store ids of the app split's signals (the shared projectKindContext
// `modules`, `owner/key`, read when the home boots — a row with no name is
// named by its key). Each `{ key, name, owner }`, once per owner/key, a named
// row first; where the shell's store-copy resolver runs (storeItemText, the
// shared one every shell surface names an app with), also `label`: the app's
// name as a person sees it in this language — "ხალხი" for hr-app in Georgian,
// "Products" for catalog-app — which a name matches as it matches `name`, and
// which the card says.
export function canvasAnswerStoreApps(root) {
  const el = this && typeof this.call === 'function' ? this : null
  const r = root && typeof root === 'object' ? root : {}
  const fns = el && el.context && el.context.functions
  const named = !!fns && typeof fns.storeItemText === 'function'
  const rows = []
  const seen = new Set()
  const split = (id) => {
    const s = typeof id === 'string' ? id : ''
    const at = s.indexOf('/')
    return at > 0 ? [s.slice(0, at), s.slice(at + 1)] : ['', '']
  }
  const add = (owner, key, name) => {
    const k = typeof key === 'string' ? key.trim() : ''
    if (!k) return
    const o = typeof owner === 'string' ? owner.trim() : ''
    const id = (o + '/' + k).toLowerCase()
    if (seen.has(id)) return
    seen.add(id)
    const row = {
      key: k,
      name: typeof name === 'string' && name.trim() ? name.trim() : k,
      owner: o
    }
    if (named) {
      let label = ''
      try {
        label = el.call('storeItemText', { key: k, name: row.name }, 'name')
      } catch (_) {
        label = ''
      }
      if (typeof label === 'string' && label.trim()) row.label = label.trim()
    }
    rows.push(row)
  }
  for (const a of Array.isArray(r._wsAppsCatalog) ? r._wsAppsCatalog : []) {
    if (!a || typeof a !== 'object') continue
    const [owner, key] = split(a.id)
    add(a.owner || owner, a.key || key, a.name)
  }
  let ctx = null
  if (el) {
    try {
      ctx = el.call('projectKindContext', r)
    } catch (_) {
      ctx = null
    }
  }
  for (const id of ctx && Array.isArray(ctx.modules) ? ctx.modules : []) {
    const [owner, key] = split(id)
    add(owner, key, '')
  }
  return rows
}

// WHAT A NAME MEANS to a site view — the ONE rule of every website card
// (site-preview, project-pages, design-system, project-status) and of their
// links: `{ kind, site, name, match }`, `kind`
//   'website'   one of `sites` (canvasAnswerSites rows) — `site` is its row
//   'project'   a project of this workspace that is not a website (an
//               installed app, a module, a library; `projects` = the
//               workspace's list) — `name` its name, else its key; an App
//               Store app INSTALLED here (installed AND an App Store row with
//               the same owner/key) also answers to, and is named by, its App
//               Store name (`label`, canvasAnswerStoreApps) — never a
//               project that only shares a key with an App Store app
//   'storeApp'  an App Store app that is no project of this workspace
//               (canvasAnswerStoreApps) — `name` its label, else its name,
//               else its key; `app` its `{ owner, key }` (the card's store
//               link, canvasAnswerStoreLink)
//   null        nothing this workspace knows by that name.
// `match`: 'exact' or 'part' — how the asked name matched — else null (no
// name asked, or no match).
// An EXACT key or name wins over a part of one, in every list (item 4,
// 2026-10-31): an exact website, an exact project, an exact App Store app.
// Then A PART (MISSING-NAMES-2, 2026-10-31): a part of a website, else of a
// project — a project over a website only when its span (the words of the
// asked text the part covers, canvasAnswerPickSite 'span') holds the
// website's and more: "CRM Landing App page" names the app "CRM Landing App"
// over the website "Landing"; "Shop CRM Landing" keeps the website "Shop CRM"
// over the app "CRM Landing". A WORKSPACE ITEM ALWAYS BEATS A PART OF AN APP
// STORE APP (the concierge, 2026-10-31: trailing generic words, a large
// catalog): a part of an App Store app counts only when no website and no
// project matches by part — inside the App Store the longest span wins, then
// the list order. A match alone writes nothing (an install keeps its own
// confirm). `projects` not a list (a failed read): websites only — the card
// cannot tell what the workspace has. No query: canvasAnswerPickSite's own
// rule (the website on screen; with no project on screen, the first website;
// a project on screen that is no website: none). The match itself is
// canvasAnswerPickSite's (its `how`), never a second copy.
export function canvasAnswerNamed(sites, projects, query, onScreen, root) {
  const el = this
  const q = String(query == null ? '' : query).trim()
  const web = (Array.isArray(sites) ? sites : []).filter((x) => x && x.key)
  const pick = (rows, how) => el.call('canvasAnswerPickSite', rows, q, onScreen, how)
  const as = (kind, row, match) =>
    row
      ? {
          kind,
          site: kind === 'website' ? row : null,
          name: row.label || row.name || String(row.key),
          match,
          ...(kind === 'storeApp' ? { app: { owner: row.owner || '', key: String(row.key) } } : {})
        }
      : null
  const nothing = { kind: null, site: null, name: '', match: null }
  const known = Array.isArray(projects) ? projects.filter((p) => p && p.key) : null
  if (!q) return as('website', pick(web), null) || nothing
  if (!known) {
    return (
      as('website', pick(web, 'exact'), 'exact') ||
      as('website', pick(web, 'part'), 'part') ||
      nothing
    )
  }
  const slugOf = (v) => {
    if (!v) return ''
    if (typeof v === 'string') return /^[a-f0-9]{24}$/i.test(v) ? '' : v
    return v.slug || v.key || ''
  }
  const lc = (v) => String(v || '').toLowerCase()
  const ownerOf = (p) => slugOf(p.owner) || slugOf(p.workspace && p.workspace.organization)
  const siteKeys = new Set(web.map((x) => String(x.key)))
  const listed = el.call('canvasAnswerStoreApps', root)
  const storeRows = (Array.isArray(listed) ? listed : []).filter((a) => a && a.key)
  const installed = el.call('canvasAnswerInstalled', root)
  const installedIds = new Set((Array.isArray(installed) ? installed : []).map(lc))
  // The workspace's other projects, named as a person sees them (never an
  // id); an App Store app installed here by its App Store name too.
  const others = known
    .filter((p) => !siteKeys.has(String(p.key)))
    .map((p) => {
      const row = {
        key: String(p.key),
        name: typeof p.name === 'string' && p.name.trim() ? p.name.trim() : String(p.key)
      }
      const id = lc(ownerOf(p) + '/' + p.key)
      const app = installedIds.has(id)
        ? storeRows.find((a) => a.label && lc(a.owner + '/' + a.key) === id)
        : null
      return app ? { ...row, label: app.label } : row
    })
  // An App Store app that is a project here is that project: the same key, and
  // the same owner when both are known.
  const here = known.map((p) => ({ key: lc(p.key), owner: lc(ownerOf(p)) }))
  const store = storeRows.filter(
    (a) =>
      !here.some((h) => h.key === lc(a.key) && (!h.owner || !a.owner || h.owner === lc(a.owner)))
  )
  const exact =
    as('website', pick(web, 'exact'), 'exact') ||
    as('project', pick(others, 'exact'), 'exact') ||
    as('storeApp', pick(store, 'exact'), 'exact')
  if (exact) return exact
  // `a` holds `b`: its span covers b's and more.
  const holds = (a, b) => a.from <= b.from && a.to >= b.to && a.to - a.from > b.to - b.from
  const webPart = pick(web, 'span')
  const projectPart = pick(others, 'span')
  if (projectPart && (!webPart || holds(projectPart, webPart))) {
    return as('project', projectPart.row, 'part')
  }
  if (webPart) return as('website', webPart.row, 'part')
  const storePart = pick(store, 'span')
  return (storePart && as('storeApp', storePart.row, 'part')) || nothing
}

// WHAT A NAME MEANS, with the App Store list when the card needs it — the
// loads of the website cards ask this (their links stay with
// canvasAnswerNamed: a link reads nothing). canvasAnswerNamed first; when no
// EXACT name matches an asked name — nothing matches (the card would say "no
// match"), or only a part does (an exact App Store app the list holds wins
// over a part: MISSING-NAMES-2, 2026-10-31) — the project list was read, and
// the shell has not read the App Store list yet (`root._wsAppsCatalogLoaded`),
// the shell's own cached read runs ONCE (loadWorkspaceAppsCatalog, SDK
// listPublicProjects — workspace ai's word, 2026-10-31) and the name is
// decided again. An exact website or project never reads. A read already in
// flight (the apps hub, the welcome, the search, another card) makes that
// loader return at once with no handle on its read, so this waits for THAT
// read — a 100 ms timer step on the flags the loader clears, at most `waitMs`
// (8 s) — and never sends a second one. A host with no loader (no shell), a
// failed or empty read, a wait past its bound: the first answer. The answer
// stays on the card (`__canvasNamed`: the asked name, the answer, the two
// lists it rests on) for canvasAnswerNamedMoved. Never throws.
export async function canvasAnswerNamedRead(sites, projects, query, onScreen, root, waitMs) {
  const el = this
  const nothing = { kind: null, site: null, name: '', match: null }
  const r = root && typeof root === 'object' ? root : null
  const asked = String(query == null ? '' : query).trim()
  const kept = (meant) => {
    if (el && typeof el === 'object') {
      el.__canvasNamed = {
        query: asked,
        answer: JSON.stringify([
          meant.kind || null,
          meant.site ? String(meant.site.key) : null,
          meant.name || ''
        ]),
        projects: r ? r._projectsForActiveOrg : undefined,
        catalog: r ? r._wsAppsCatalog : undefined
      }
    }
    return meant
  }
  let first = nothing
  try {
    first = el.call('canvasAnswerNamed', sites, projects, query, onScreen, root) || nothing
  } catch (_) {
    return nothing
  }
  if (first.match === 'exact' || !asked || !r || !Array.isArray(projects)) return kept(first)
  if (r._wsAppsCatalogLoaded === true) return kept(first)
  const fns = el && el.context && el.context.functions
  if (!fns || typeof fns.loadWorkspaceAppsCatalog !== 'function') return kept(first)
  try {
    await el.call('loadWorkspaceAppsCatalog')
    const end = Date.now() + (Number(waitMs) > 0 ? Number(waitMs) : 8000)
    while (r._wsAppsCatalogLoading === true && r._wsAppsCatalogLoaded !== true) {
      if (Date.now() >= end) return kept(first)
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
    if (r._wsAppsCatalogLoaded !== true) return kept(first)
    return kept(el.call('canvasAnswerNamed', sites, projects, query, onScreen, r) || first)
  } catch (_) {
    return kept(first)
  }
}

// THE ANSWER MOVED — the first step of every website card's load
// (site-preview, project-pages, design-system, project-status). What an asked
// name means rests on two lists that can land after the card answered: the
// project list (`root._projectsForActiveOrg`) and the App Store list
// (`root._wsAppsCatalog` — another surface read it, or this card's own read
// came back after its wait). The card subscribes to both, but its load
// signature holds neither, so this decides again — sync, no read
// (canvasAnswerNamed) — once per list identity, for the name the card asked
// (`query`; canvasAnswerNamedRead keeps the answer on the card). A different
// answer bumps the card's counter (`__canvasNamedEpoch`), which is part of its
// load signature, so the load runs again — a run in flight drops at its own
// signature check; the same answer changes nothing. Returns the counter.
export function canvasAnswerNamedMoved(el, query) {
  const host = el || this
  if (!host || typeof host !== 'object' || typeof host.call !== 'function') return 0
  const epoch = Number(host.__canvasNamedEpoch) || 0
  const was = host.__canvasNamed
  const root = host.state && host.state.root
  const asked = String(query == null ? '' : query).trim()
  if (!was || !root || typeof root !== 'object' || was.query !== asked) return epoch
  if (was.projects === root._projectsForActiveOrg && was.catalog === root._wsAppsCatalog) {
    return epoch
  }
  was.projects = root._projectsForActiveOrg
  was.catalog = root._wsAppsCatalog
  let meant = null
  try {
    const list = host.call('canvasAnswerProjects', root)
    if (!Array.isArray(list)) return epoch
    const sites = host.call('canvasAnswerSites', list, host.call('canvasAnswerInstalled', root))
    const onScreen = host.call('canvasAnswerOnScreen', root)
    meant = host.call('canvasAnswerNamed', sites, list, asked, onScreen, root)
  } catch (_) {
    return epoch
  }
  const m = meant && typeof meant === 'object' ? meant : {}
  const answer = JSON.stringify([m.kind || null, m.site ? String(m.site.key) : null, m.name || ''])
  if (answer === was.answer) return epoch
  host.__canvasNamedEpoch = epoch + 1
  return epoch + 1
}

// AN APP STORE APP'S PAGE — the card's link when the asked name is an App
// Store app that is no project here (canvasAnswerNamed `app`): the App
// Store's own item deep link, from the App Store's one builder (marketplace
// functions/appStoreItemLink.js `appStoreItemLink`, registered in the shell
// at boot; canvas standalone folds the marketplace in), the one the search's
// App Store rows and the /add-app catalog card ask too. The card names the
// app as an App Store app and links there; the install keeps its own confirm
// on that page. null without an owner and a key, or with one a slug has not
// (the builder's rule).
export function canvasAnswerStoreLink(app) {
  const a = app && typeof app === 'object' ? app : {}
  return this.call('appStoreItemLink', a.owner, a.key)
}

// The `project` param of a site view: an ENUM of the workspace's websites —
// values their keys, labels their NAMES (the answer line names a site, never a
// raw key — QA 2026-10-18 D9) — once the list is known
// (`root._projectsForActiveOrg`), a name-or-key STRING before. `valuesLive`:
// the list is this workspace's, as the shell read it, so a saved page keeps a
// site the list does not name now (assistantBoard.js validateBoardSpec); the
// view maps the value itself. The describe is MODEL-FACING (never rendered):
// with a project on screen that is not one of the websites (an app canvas),
// a view left without `project` shows no site (canvasAnswerPickSite), and the
// describe says so — the model names a website, or leaves the view out.
export function canvasAnswerProjectParam(root) {
  let describe =
    'a website (project) of this workspace; the project on screen, else the newest website'
  const list = this.call('canvasAnswerProjects', root)
  if (!Array.isArray(list)) return { type: 'string', describe }
  const sites = this.call('canvasAnswerSites', list, this.call('canvasAnswerInstalled', root))
  const on = this.call('canvasAnswerOnScreen', root)
  if (on && on.key && !this.call('canvasAnswerPickSite', sites, '', on)) {
    describe =
      'a website (project) of this workspace; the project on screen is not one, so none shows when left out'
  }
  if (!sites.length) return { type: 'string', describe }
  const values = []
  const labels = {}
  for (const s of sites) {
    if (values.includes(s.key)) continue
    values.push(s.key)
    labels[s.key] = s.name
  }
  return { type: 'enum', values, labels, valuesLive: true, describe }
}

// The view's read, once per params (its OWN state: { projects, failed }).
export async function canvasAnswerLoad(el) {
  const host = el || this
  if (!host || !host.state || typeof host.state.update !== 'function') return null
  const params = (host.scope && host.scope.params) || {}
  const sig = JSON.stringify([params.search || '', params.limit || 6, params.env || ''])
  if (host.__canvasAnswerSig === sig) return null
  host.__canvasAnswerSig = sig
  const res = await host.call('canvasAnswerRead', host)
  if (host.__canvasAnswerSig !== sig) return null
  const list = host.call('canvasAnswerCards', res && res.projects, params)
  const total = host.call('canvasAnswerMatchCount', res && res.projects, params)
  host.state.update({ projects: list, total, failed: !!(res && res.failed) && !list.length })
  return res
}

// How many projects the search matches (the cards show the newest `limit`):
// "6 of 79" on the view, never a silent cut (live on dev, Symbols Intranet).
export function canvasAnswerMatchCount(projects, params) {
  const p = params && typeof params === 'object' ? params : {}
  const words = String(p.search || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
  return (Array.isArray(projects) ? projects : []).filter((x) => {
    if (!x || !x.key) return false
    const hay = (String(x.name || '') + ' ' + String(x.key)).toLowerCase()
    return words.every((w) => hay.includes(w))
  }).length
}

// The MORE line's words — "6 of 79 projects, newest first" — only when the
// cards show SOME of the matches and not all; '' otherwise (reading, none, or
// every match shown), and the line hides on ''. An empty view carries no
// footer sentence at all (Fly QA 2026-10-18: an empty tasks card still held
// "The 0 most recently updated…" as its hidden text; the same rule here).
export function canvasAnswerMoreText(s) {
  const list = s && Array.isArray(s.projects) ? s.projects : null
  const total = Number(s && s.total) || 0
  if (!list || !list.length || !(total > list.length)) return ''
  return String(this.call('polyglot', 'canvas.answer.more'))
    .replace('{n}', String(list.length))
    .replace('{total}', String(total))
}

// The cards: the search applied, newest first, `limit` of them, each in the
// shared ProjectCard's own shape plus the environment its live preview opens.
// Every key the card reads is set (a childrenAs-'state' card falls back to
// ROOT for a missing one — `workspace` above all).
export function canvasAnswerCards(projects, params) {
  const el = this
  const p = params && typeof params === 'object' ? params : {}
  const list = Array.isArray(projects) ? projects.filter(Boolean) : []
  const slugOf = (v) => {
    if (!v) return ''
    if (typeof v === 'string') return /^[a-f0-9]{24}$/i.test(v) ? '' : v
    return v.slug || v.key || ''
  }
  const root = (el && el.state && el.state.root) || {}
  const claims = root.jwtClaims || {}
  const orgs = claims.orgs || root.orgs || []
  const org = (Array.isArray(orgs) ? orgs : []).find((o) => o && o.id === claims.workspace_org_id)
  const ownerSlug = (org && org.slug) || ''
  const wsId = claims.active_workspace_id || (root.workspace && root.workspace.id) || null
  const wsList = Array.isArray(claims.workspaces) ? claims.workspaces : []
  const wsEntry = wsList.find((w) => w && w.id === wsId)
  const wsSlug = (wsEntry && wsEntry.slug) || ''
  const words = String(p.search || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
  const limit = Math.max(1, Math.min(24, Math.floor(Number(p.limit)) || 6))
  const env = ['production', 'staging', 'development'].includes(p.env) ? p.env : 'production'
  const at = (x) =>
    Date.parse(x.updatedAt || x.updated_at || x.createdAt || x.created_at || '') || 0
  // THE APP SPLIT (docs/APP_SPLIT.md): this view is the WEBSITES view — a
  // workspace app (a module, an AI-built app) and a library never become a
  // card here (the shared projectKind, with the workspace's own signals).
  let kindCtx = null
  if (el && typeof el.call === 'function' && el.state && el.state.root) {
    try {
      kindCtx = el.call('projectKindContext', el.state.root) || null
    } catch (_) {
      kindCtx = null
    }
  }
  return list
    .filter((x) => {
      if (!x.key) return false
      if (kindCtx) {
        // An unregistered classifier answers nothing — the card then stays.
        const kind = el.call('projectKind', x, kindCtx)
        if (typeof kind === 'string' && kind !== 'website') return false
      }
      const hay = (String(x.name || '') + ' ' + String(x.key)).toLowerCase()
      return words.every((w) => hay.includes(w))
    })
    .sort((a, b) => at(b) - at(a))
    .slice(0, limit)
    .map((x) => ({
      key: String(x.key || ''),
      name: x.name || '',
      description: x.description || '',
      icon: x.icon || null,
      owner: slugOf(x.owner) || slugOf(x.workspace && x.workspace.organization) || ownerSlug,
      workspace: x.workspace || null,
      workspaceSlug: slugOf(x.workspaceSlug) || wsSlug,
      env
    }))
}

// A card's actions (read-only — what the Studio card offers, without its
// GitHub): `row` is the card's state.
//   preview    the project's live preview in the sheet (⌘ / Ctrl: in a split)
//              — the preview package's own open (previewAnswerOpenSite)
//   canvas     the project, opened the way its /studio tile opens it
//              (studioOpenProject). The cards list websites only and carry
//              no db `_id` (the ProjectCard's shape), so a card takes the
//              project's route: `/<owner>/<key>` or the workspace-scoped form
//   analytics  the project's analytics in the sheet (`_analyticsParams`, the
//              Studio sidebar's own carrier)
// false when the row names no project or the action is unknown.
export function canvasAnswerOpen(row, action, event) {
  const el = this
  const s = row && typeof row === 'object' ? row : {}
  const owner = typeof s.owner === 'string' ? s.owner : ''
  const key = typeof s.key === 'string' ? s.key.split('.')[0] : ''
  if (!el || !owner || !key) return false
  const root = el.state && el.state.root
  if (action === 'preview') {
    const env = ['production', 'staging', 'development'].includes(s.env) ? s.env : null
    el.call('previewAnswerOpenSite', { owner, key, env }, event)
    return true
  }
  if (action === 'canvas') {
    return el.call('studioOpenProject', { owner, key, _id: s._id }) === true
  }
  if (action === 'analytics') {
    if (!root || typeof root.update !== 'function') return false
    root.update({ _analyticsParams: { owner, key }, _analyticsProjectKey: key })
    const split = !!(event && (event.metaKey || event.ctrlKey))
    el.call('openAppPage', '/analytics', split ? { split: true } : undefined)
    return true
  }
  return false
}
